import React, { useState, useRef } from 'react';
import { Share2, Twitter, Facebook, Linkedin, MessageCircle, Link2, Check, Download, Copy } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShareCard = ({ article }) => {
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const cardRef = useRef(null);
  
  const shareUrl = `${(process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '')}/news/${article.slug || article.id}`;
  const shareText = `"${article.humorous_summary}" - Daily Wit`;

  const handleShare = (platform) => {
    let shareLink = '';
    
    switch (platform) {
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&hashtags=DailyWit,${article.category}`;
        break;
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'linkedin':
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareLink, '_blank', 'width=600,height=400');
    logShare(platform);
    setShowShareMenu(false);
    toast.success(`Opening ${platform}...`);
  };

  const logShare = async (platform) => {
    try {
      await fetch(`${BACKEND_URL}/api/share/${article.id}?platform=${platform}`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Error logging share:', error);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      logShare('link');
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleCopyQuote = async () => {
    const quote = `"${article.humorous_summary}"\n\n— Daily Wit\n${shareUrl}`;
    try {
      await navigator.clipboard.writeText(quote);
      toast.success('Quote copied!');
    } catch (error) {
      toast.error('Failed to copy quote');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: shareText,
          url: shareUrl
        });
        logShare('native');
      } catch (error) {
        if (error.name !== 'AbortError') {
          setShowShareMenu(!showShareMenu);
        }
      }
    } else {
      setShowShareMenu(!showShareMenu);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#FF6B6B]/10 to-[#4ECDC4]/10 border-2 border-black dark:border-white rounded-xl p-6 mt-8">
      <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
        <Share2 className="w-5 h-5" />
        Share This Wit
      </h3>
      
      {/* Preview Card */}
      <div 
        ref={cardRef}
        className="bg-white dark:bg-[#2D2A26] border-2 border-black rounded-xl overflow-hidden mb-4"
        data-testid="share-preview-card"
      >
        {article.image_url && (
          <img 
            src={article.image_url} 
            alt={article.title}
            className="w-full h-40 object-cover"
          />
        )}
        <div className="p-4">
          <p className="text-xs uppercase font-bold text-[#FF6B6B] mb-2">{article.category}</p>
          <p 
            className="text-lg font-bold italic text-gray-800 dark:text-gray-200 mb-2"
            style={{ fontFamily: 'Merriweather, serif' }}
          >
            "{article.humorous_summary}"
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">— Daily Wit</p>
        </div>
      </div>

      {/* Share Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => handleShare('twitter')}
          data-testid="share-card-twitter"
          className="flex items-center gap-2 px-4 py-2 bg-[#1DA1F2] text-white rounded-lg font-bold text-sm hover:bg-[#1a8cd8] transition-all"
        >
          <Twitter className="w-4 h-4" />
          Twitter/X
        </button>
        <button
          onClick={() => handleShare('facebook')}
          data-testid="share-card-facebook"
          className="flex items-center gap-2 px-4 py-2 bg-[#4267B2] text-white rounded-lg font-bold text-sm hover:bg-[#365899] transition-all"
        >
          <Facebook className="w-4 h-4" />
          Facebook
        </button>
        <button
          onClick={() => handleShare('linkedin')}
          data-testid="share-card-linkedin"
          className="flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white rounded-lg font-bold text-sm hover:bg-[#006699] transition-all"
        >
          <Linkedin className="w-4 h-4" />
          LinkedIn
        </button>
        <button
          onClick={() => handleShare('whatsapp')}
          data-testid="share-card-whatsapp"
          className="flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white rounded-lg font-bold text-sm hover:bg-[#20bd5a] transition-all"
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp
        </button>
      </div>

      {/* Copy Actions */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleCopyLink}
          data-testid="share-card-copy-link"
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg font-bold text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-all border-2 border-black dark:border-white"
        >
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
        <button
          onClick={handleCopyQuote}
          data-testid="share-card-copy-quote"
          className="flex items-center gap-2 px-4 py-2 bg-[#FFE66D] rounded-lg font-bold text-sm hover:bg-[#FFD93D] transition-all border-2 border-black"
        >
          <Copy className="w-4 h-4" />
          Copy Quote
        </button>
      </div>

      {/* RSS Tip */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          📡 Want to auto-post? Use our <a href={`${BACKEND_URL}/api/rss`} target="_blank" rel="noopener noreferrer" className="text-[#4ECDC4] hover:underline">RSS feed</a> with IFTTT or Zapier!
        </p>
      </div>
    </div>
  );
};

export default ShareCard;
